import { CurrencyPipe, PercentPipe } from "@angular/common";
import { Injectable } from "@angular/core";
import { HorizontalListFactory } from "../../../../ui/horizontal-list/horizontal-list.factory";
import { HorizontalListData, HorizontalListItemSize } from "../../../../ui/horizontal-list/horizontal-list.model";
import { OfferCalculator } from "../../../services/offer-calculator";
import { getOfferTermLabel } from "../../../pipes/offer-term-label-pipe";
import { HorizontalListActions } from "./offer-calculator-details-table.model";

@Injectable()
export class OfferCalculatorDetailsTableAdapter {
  constructor(
    private readonly currencyPipe: CurrencyPipe,
    private readonly percentPipe: PercentPipe,
  ) { }

  adaptDetailsListData = (offerCalculator: OfferCalculator, showTerm: boolean, showCommission: boolean): HorizontalListData[] => {
    return [
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Credit limit',
        displayedValue: this.formatCurrency(offerCalculator?.amount),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Term',
        displayedValue: getOfferTermLabel(offerCalculator.term),
        isItemHidden: !showTerm
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Sample Draw',
        displayedValue: this.formatCurrency(offerCalculator?.sampleDraw)
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: `Fee (${offerCalculator.drawFeeFormatted})`,
        displayedValue: this.formatCurrency(offerCalculator?.drawFee)
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Origination fee',
        displayedValue: this.formatCurrency(offerCalculator?.originationFeeAmount),
        isItemHidden: !offerCalculator?.originationFeeAmount
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Maintenance fee',
        displayedValue: this.formatCurrency(offerCalculator?.maintenanceFeeAmount),
        isItemHidden: !offerCalculator?.maintenanceFeeAmount
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Maintenance fee frequency',
        displayedValue: offerCalculator.maintenanceFeeFrequencyName,
        isItemHidden: !offerCalculator?.maintenanceFeeAmount
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Disbursed amount',
        displayedValue: this.formatCurrency(offerCalculator?.disbursedAmount),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Daily finance charges',
        displayedValue: this.percentPipe.transform(offerCalculator?.dailyInterestRate, '1.4-4'),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: `${offerCalculator.paymentFrequencyName} payment`,
        displayedValue: this.formatCurrency(offerCalculator?.termPayment),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'No payment period',
        displayedValue: `${offerCalculator.noPaymentTermsValue} ${offerCalculator.noPaymentTermsName}`,
        isItemHidden: !offerCalculator.noPaymentTermsValue
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Interest only period',
        displayedValue: `${offerCalculator.interestOnlyPaymentTermsValue} ${offerCalculator.interestOnlyPaymentTermsName}`,
        isItemHidden: !offerCalculator.interestOnlyPaymentTermsValue
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: `Commissions (${this.percentPipe.transform(offerCalculator.brokerInitialDrawCommission, '1.2-2')})`,
        displayedValue: this.formatCurrency(offerCalculator?.commission),
        isItemHidden: !showCommission
      }),
    ]
  }

  adaptCostListData = (offerCalculator: OfferCalculator): HorizontalListData[] => {
    return [
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Finance charges paid',
        displayedValue: this.formatCurrency(offerCalculator?.sampleDuration?.interest),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Payback',
        displayedValue: this.formatCurrency(offerCalculator?.sampleDuration?.payback),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Monthly finance cost*',
        displayedValue: this.percentPipe.transform(offerCalculator?.monthlyRate, '1.2-2'),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Annual finance cost*',
        displayedValue: this.percentPipe.transform(offerCalculator?.annualRate, '1.2-2'),
      }),
      HorizontalListFactory.createInfoStringItem({
        displayedValue: '* The cost of finance is calculated based on the finance charges paid over the life of the loan, assuming an organic repayment.',
        hideDividerBelow: true,
        itemSize: HorizontalListItemSize.Small
      }),
      HorizontalListFactory.createInfoExternalLinkItem({
        displayedValue: 'Click here to read disclosures required by law.',
        actionName: HorizontalListActions.ShowDisclosures,
        isItemHidden: !offerCalculator.hasDisclosures
      }),
    ]
  }

  private formatCurrency(value: number): string {
    return this.currencyPipe.transform(value, 'USD', 'symbol', '1.2-2');
  }
}
